// src/utils/authStorage.js
import { jwtDecode } from "jwt-decode";

const PENDING_KEY = "pendingAuth";

/** Stash unverified login/SSO data. Session-only, cleared on tab close,
 *  and — critically — never read by ProtectedRoute, so it can never
 *  substitute for a real session. */
export function savePendingAuth(data) {
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(data));
}

export function getPendingAuth() {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearPendingAuth() {
  sessionStorage.removeItem(PENDING_KEY);
}

/** Best-effort email extraction for the OTP screen, without persisting
 *  anything. */
export function getPendingEmail(pending) {
  if (!pending) return null;
  if (pending.authType === "sso") {
    return pending.payload?.email || pending.payload?.Email || null;
  }
  try {
    const decoded = jwtDecode(pending.token);
    return decoded?.email || decoded?.Email || null;
  } catch {
    return null;
  }
}

function persistCommon({ token, decodedToken, filePath, rights }) {
  localStorage.setItem("token", JSON.stringify(token));

  // If the user previously uploaded a logo, it is stored as a DataURL in
  // uploadedLogoDataUrl. Prefer that over the server-returned companyLogoPath
  // so that login NEVER reverts the sidebar logo to the server's default.
  const cachedDataUrl = localStorage.getItem("uploadedLogoDataUrl");
  const effectiveFilePath =
    cachedDataUrl && cachedDataUrl !== "null" && cachedDataUrl !== "undefined"
      ? cachedDataUrl
      : filePath ?? null;
  localStorage.setItem("filePath", effectiveFilePath);

  const expirationTime = decodedToken?.exp
    ? decodedToken.exp * 1000
    : Date.now() + 5 * 60 * 60 * 1000;
  localStorage.setItem("tokenExpiration", expirationTime);

  if (rights) {
    const cleanedData = JSON.parse(rights.replace(/\\/g, ""));
    localStorage.setItem("rights", JSON.stringify(rights));
    localStorage.setItem("rightsforrbac", cleanedData);
  }

  localStorage.setItem("decodedToken", JSON.stringify(decodedToken));
  return decodedToken;
}

/** Call this ONLY after OTP verification succeeds for the normal
 *  username/password flow (real JWT from the API). */
export function commitJwtAuth(pending) {
  const { token, companyLogoPath, rights } = pending;
  const decodedToken = jwtDecode(token);
  return persistCommon({ token, decodedToken, filePath: companyLogoPath, rights });
}

/** Call this ONLY after OTP verification succeeds for the SSO
 *  query-param flow (base64 JSON claims, not a real JWT). */
export function commitSsoAuth(pending) {
  const { rawToken, payload } = pending;
  if (payload?.systemAToken) {
    localStorage.setItem("systemAToken", payload.systemAToken);
  }
  return persistCommon({
    token: rawToken,
    decodedToken: payload,
    filePath: null,
    rights: null,
  });
}

export function isAuthenticated() {
  const token = localStorage.getItem("token");
  const expiration = localStorage.getItem("tokenExpiration");
  if (!token || !expiration) return false;
  return Date.now() < Number(expiration);
}

export function clearAuth() {
  [
    "token",
    // NOTE: "filePath" and "uploadedLogoDataUrl" are intentionally NOT cleared here.
    // They must persist across logout/login so the uploaded logo is never lost.
    "tokenExpiration",
    "rights",
    "rightsforrbac",
    "decodedToken",
    "systemAToken",
  ].forEach((k) => localStorage.removeItem(k));
}